import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCelebration } from '@/context/CelebrationContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Check, Heart, Sparkles, Zap, Target, Star, Users, BookOpen, Video, Calendar, Moon, Briefcase, MessageCircle, Lock, Play, PenLine, Compass } from 'lucide-react';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, updateProfile, refreshUser } = useAuth();
  const { celebrate, grandCelebrate, epicCelebrate } = useCelebration();
  const [step, setStep] = useState(0); // Start at 0 for welcome message
  const [formData, setFormData] = useState({
    community_agreement_accepted: false,
    focus_area: '',
    notification_preferences: {
      messages: true,
      community: true,
      events: true,
      marketing: false
    }
  });
  const [loading, setLoading] = useState(false);
  const [completionInProgress, setCompletionInProgress] = useState(false);
  const [celebrationTriggered, setCelebrationTriggered] = useState({});

  const totalSteps = 6; // Now 6 steps with dedicated welcome step

  const focusAreas = [
    { id: 'career', label: 'Career Clarity', icon: Briefcase, description: 'Job transitions, leadership growth, or finding purpose in work' },
    { id: 'boundaries', label: 'Boundaries & Relationships', icon: Users, description: 'Setting limits, navigating difficult dynamics, finding peace' },
    { id: 'faith', label: 'Faith & Discernment', icon: Heart, description: "Spiritual grounding, trusting God's timing, seeking wisdom" },
    { id: 'rest', label: 'Rest & Nervous System', icon: Moon, description: 'Burnout recovery, stress management, finding stillness' },
    { id: 'visibility', label: 'Visibility & Leadership', icon: Star, description: 'Personal brand, showing up authentically, stepping into influence' }
  ];

  const handleNext = async () => {
    // Prevent double-submission
    if (completionInProgress) return;
    
    if (step === 2 && !formData.community_agreement_accepted) {
      toast.error('Please accept the community agreement to continue');
      return;
    }
    if (step === 3 && !formData.focus_area) {
      toast.error('Please select a focus area');
      return;
    }

    // Celebrate step transitions (only once per step)
    if (step === 2 && formData.community_agreement_accepted && !celebrationTriggered[2]) {
      setCelebrationTriggered(prev => ({ ...prev, 2: true }));
      celebrate('hearts', "You're officially part of the sisterhood! 💕");
    } else if (step === 3 && formData.focus_area && !celebrationTriggered[3]) {
      setCelebrationTriggered(prev => ({ ...prev, 3: true }));
      grandCelebrate('sparkle');
    }

    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      // Complete onboarding
      setLoading(true);
      setCompletionInProgress(true);
      try {
        await updateProfile({
          ...formData,
          onboarding_completed: true
        });
        // Refresh user data to ensure onboarding_completed is synced
        await refreshUser();
        // EPIC celebration for completing onboarding!
        epicCelebrate("Welcome Home, Sis! Your sanctuary is ready! 💎👑✨");
        // Navigate after a delay for celebration
        setTimeout(() => navigate('/dashboard', { replace: true }), 3000);
      } catch (error) {
        console.error('Onboarding completion error:', error);
        toast.error('Failed to complete onboarding. Please try again.');
        setLoading(false);
        setCompletionInProgress(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  // Redirect if onboarding is already completed
  if (user?.onboarding_completed && !completionInProgress) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-500">Step {step + 1} of {totalSteps}</span>
            <span className="text-sm text-forest font-medium">
              {step === 0 && 'Welcome Home'}
              {step === 1 && 'You Belong Here'}
              {step === 2 && 'Community Agreement'}
              {step === 3 && 'Your Focus'}
              {step === 4 && 'Meet Your Space'}
              {step === 5 && 'Your First Steps'}
            </span>
          </div>
          <div className="h-2 bg-sand rounded-full">
            <div 
              className="h-2 bg-terracotta rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        <Card className="card-base shadow-lg">
          {/* Step 0: Welcome Home - Detailed Welcome Message */}
          {step === 0 && (
            <>
              <CardHeader className="text-center pb-2">
                <CardTitle className="font-heading text-3xl text-forest">
                  Welcome Home, {user?.full_name?.split(' ')[0] || 'Sis'}
                </CardTitle>
                <CardDescription className="text-base mt-2">
                  You just made one of the most powerful decisions of your life.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Welcome Video from Crystal & Dr. Nicole */}
                <div className="relative rounded-xl overflow-hidden">
                  <div style={{ padding: '177.78% 0 0 0', position: 'relative' }}>
                    <iframe 
                      src="https://player.vimeo.com/video/1166067182?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=0"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                      title="Welcome to Sister Diamonds"
                    />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-terracotta/5 to-forest/5 rounded-xl p-6 text-center">
                  <p className="text-lg text-forest leading-relaxed mb-4">
                    You are not here by accident. Something in your spirit said <span className="font-semibold italic">&ldquo;it&apos;s time&rdquo;</span> — time to be seen, held, and celebrated for the brilliant woman you already are.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    Sister Diamonds is not another place asking you to perform, prove, or produce. This is your sanctuary — a sisterhood of high-capacity Black women who understand the weight you carry and the glory you embody.
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-sm text-gray-500 italic">
                    "You don't have to carry it all alone anymore."
                  </p>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 1: You Belong Here */}
          {step === 1 && (
            <>
              <CardHeader className="text-center">
                <div className="w-20 h-20 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="h-10 w-10 text-terracotta" />
                </div>
                <CardTitle className="font-heading text-3xl text-forest">You Belong Here, Sis</CardTitle>
                <CardDescription className="text-base">
                  {user?.full_name?.split(' ')[0]}, you&apos;ve just stepped into a sanctuary of high-capacity, brilliant Black women who see you.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-center text-gray-600 leading-relaxed">
                  This is your space to breathe, become, and soar. Over the next 90 days, 
                  you&apos;ll have access to soul-nourishing resources designed specifically for women like us.
                </p>
                
                <div className="bg-sand/50 rounded-xl p-6">
                  <h3 className="font-heading text-lg text-forest mb-4 text-center">Here&apos;s What Awaits You:</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-terracotta/10 flex items-center justify-center flex-shrink-0">
                        <Users className="h-5 w-5 text-terracotta" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">Sister Circles</p>
                        <p className="text-sm text-gray-500">Safe spaces to speak what&apos;s sacred</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-forest/10 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="h-5 w-5 text-forest" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">Soul Journaling</p>
                        <p className="text-sm text-gray-500">Pour out your heart, track your becoming</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Video className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">The Vault</p>
                        <p className="text-sm text-gray-500">Wisdom from Crystal & Dr. Nicole</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">Sacred Gatherings</p>
                        <p className="text-sm text-gray-500">Live circles that transform</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-center text-terracotta font-medium">
                  You don&apos;t have to carry it all alone anymore.
                </p>
              </CardContent>
            </>
          )}

          {/* Step 2: Community Agreement */}
          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle className="font-heading text-2xl text-forest flex items-center gap-2">
                  <span className="text-2xl">💎</span> Our Collective Agreement
                </CardTitle>
                <CardDescription>
                  This is a sacred space. Our sisterhood thrives on trust, grace, and confidentiality. 
                  Please read and honor our community agreement.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-sand/50 rounded-xl p-6 space-y-4">
                  <h3 className="font-medium text-forest">As a Sister Diamond, I commit to:</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <span>Holding space for each sister with respect and compassion</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <span>Honoring the sacredness of shared stories and experiences</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <span>Showing up authentically, without masks or performance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <span>Offering and receiving truth with grace</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <span>Nurturing my own becoming while celebrating my sisters</span>
                    </li>
                  </ul>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="agreement" 
                    checked={formData.community_agreement_accepted}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, community_agreement_accepted: checked }))}
                  />
                  <Label htmlFor="agreement" className="text-forest">
                    I honor the Sister Diamonds community agreement
                  </Label>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: Focus Area Selection (Personal Focus Path) */}
          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle className="font-heading text-2xl text-forest">Your Personal Focus Path</CardTitle>
                <CardDescription>
                  Where does your soul need attention right now? Choose your focus — you can always explore other areas too.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={formData.focus_area} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, focus_area: value }))}
                  className="space-y-4"
                >
                  {focusAreas.map((area) => (
                    <div key={area.id} className="flex items-center space-x-3">
                      <RadioGroupItem value={area.id} id={area.id} />
                      <Label 
                        htmlFor={area.id} 
                        className="flex items-center gap-3 cursor-pointer flex-1 p-4 rounded-xl hover:bg-sand/50 transition-colors"
                      >
                        <div className="h-10 w-10 rounded-lg bg-terracotta/10 flex items-center justify-center">
                          <area.icon className="h-5 w-5 text-terracotta" />
                        </div>
                        <div>
                          <p className="font-medium text-forest">{area.label}</p>
                          <p className="text-sm text-gray-500">{area.description}</p>
                        </div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </>
          )}

          {/* Step 4: Meet Your Space - Platform Tour */}
          {step === 4 && (
            <>
              <CardHeader>
                <CardTitle className="font-heading text-2xl text-forest">Meet Your Space</CardTitle>
                <CardDescription>
                  Here's what awaits you in your sanctuary. Everything is designed for your rest and restoration.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Journal */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-purple-50 to-transparent border border-purple-100">
                  <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <PenLine className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-forest">Your Sacred Journal</h4>
                    <p className="text-sm text-gray-600">A private space to pour out your heart. Ask SHE, your AI companion, to help you go deeper.</p>
                  </div>
                </div>

                {/* The Vault */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-amber-50 to-transparent border border-amber-100">
                  <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Lock className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-forest">The Vault</h4>
                    <p className="text-sm text-gray-600">A library of soul-nourishing content — videos, teachings, and resources curated for your becoming.</p>
                  </div>
                </div>

                {/* Communities */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-rose-50 to-transparent border border-rose-100">
                  <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <Users className="h-6 w-6 text-rose-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-forest">Sister Communities</h4>
                    <p className="text-sm text-gray-600">Sacred spaces to connect, share, and be seen by sisters who truly understand.</p>
                  </div>
                </div>

                {/* Live Calls */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-teal-50 to-transparent border border-teal-100">
                  <div className="h-12 w-12 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <Video className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-forest">Live Circles & Calls</h4>
                    <p className="text-sm text-gray-600">Monthly gatherings with Dr. Crystal Nicole and guest teachers. Real-time sisterhood.</p>
                  </div>
                </div>

                {/* Personalized based on focus */}
                {formData.focus_area && (
                  <div className="mt-4 p-4 bg-forest/5 rounded-xl border border-forest/10">
                    <p className="text-sm text-forest">
                      <Compass className="h-4 w-4 inline mr-2" />
                      Based on your focus in <strong className="capitalize">{formData.focus_area.replace('_', ' & ')}</strong>, we've prepared personalized content and community recommendations for you.
                    </p>
                  </div>
                )}
              </CardContent>
            </>
          )}

          {/* Step 5: Your First Steps - Action Checklist */}
          {step === 5 && (
            <>
              <CardHeader className="text-center">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-terracotta to-forest flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="font-heading text-2xl text-forest">Your First Steps, {user?.full_name?.split(' ')[0] || 'Sis'}</CardTitle>
                <CardDescription>
                  You're all set! Here's how to begin your restoration journey.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 mb-6">
                  {/* Step 1: Journal */}
                  <div className="flex items-center gap-4 p-4 rounded-xl border-2 border-purple-200 bg-purple-50/50 hover:bg-purple-50 transition-colors">
                    <div className="h-10 w-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                      1
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-forest">Write Your First Journal Entry</h4>
                      <p className="text-sm text-gray-600">Start with: "What do I need to release today?"</p>
                    </div>
                    <PenLine className="h-5 w-5 text-purple-400" />
                  </div>

                  {/* Step 2: Vault */}
                  <div className="flex items-center gap-4 p-4 rounded-xl border-2 border-amber-200 bg-amber-50/50 hover:bg-amber-50 transition-colors">
                    <div className="h-10 w-10 rounded-full bg-amber-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                      2
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-forest">Explore The Vault</h4>
                      <p className="text-sm text-gray-600">Watch the welcome video from Dr. Crystal Nicole</p>
                    </div>
                    <Play className="h-5 w-5 text-amber-400" />
                  </div>

                  {/* Step 3: Community */}
                  <div className="flex items-center gap-4 p-4 rounded-xl border-2 border-rose-200 bg-rose-50/50 hover:bg-rose-50 transition-colors">
                    <div className="h-10 w-10 rounded-full bg-rose-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                      3
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-forest">Introduce Yourself</h4>
                      <p className="text-sm text-gray-600">Say hello to your sisters in the Inner Circle community</p>
                    </div>
                    <MessageCircle className="h-5 w-5 text-rose-400" />
                  </div>
                </div>

                {/* Focus reminder */}
                <div className="text-center p-4 bg-forest/5 rounded-xl">
                  <p className="text-sm text-gray-600">
                    Your sacred focus: <span className="font-semibold text-forest capitalize">{formData.focus_area?.replace('_', ' & ') || 'Not selected'}</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Content and recommendations are personalized for you</p>
                </div>
              </CardContent>
            </>
          )}

          {/* Navigation */}
          <div className="p-6 pt-0 flex justify-between">
            {step > 0 ? (
              <Button variant="outline" onClick={handleBack} className="btn-outline">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
            ) : (
              <div />
            )}
            <Button onClick={handleNext} className="btn-primary" disabled={loading}>
              {step === totalSteps - 1 ? (
                loading ? 'Opening the door...' : 'Step Into Your Sanctuary'
              ) : step === 0 ? (
                <>Let&apos;s Begin <ArrowRight className="ml-2 h-4 w-4" /></>
              ) : (
                <>Continue <ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;
